import * as MutationHelpers from 'helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import GiftCardAPI from '../../api/giftCards';
import { throwErrorMessage } from 'dashboard/store/utils/api';

const state = {
  records: [],
  countries: [],
  rateList: [],
  bankList: [],
  memberDetails: [],
  orderList: [],
  orderDetails: [],
  userList: [],
  uiFlags: {
    fetchingBrands: false,
    fetchingCountries: false,
    fetchingRateList: false,
    fetchingBankList: false,
    fetchingMemberDetails: false,
    creatingItem: false,
    fetchingOrderList: false,
    fetchingOrderDetails: false,
    creatingOrderWithdraw: false,
    creatingUserWithdraw: false,
    fetchingUserList: false,
  },
};

const getters = {
  getBrands(_state) {
    return _state.brands;
  },
  getCountries(_state) {
    return _state.countries;
  },
  getRateList(_state) {
    return _state.rateList;
  },
  getMemberDetails(_state) {
    return _state.memberDetails;
  },
  getOrderList(_state) {
    return _state.orderList;
  },
  getOrderDetails(_state) {
    return _state.orderDetails;
  },
  getBankList(_state) {
    return _state.bankList;
  },
  getUserList(_state) {
    return _state.userList;
  },
  getUIFlags(_state) {
    return _state.uiFlags;
  },
};

const actions = {
  async getBrands({ commit }) {
    commit(types.SET_ORDER_UI_FLAG, { fetchingBrands: true });
    try {
      const response = await GiftCardAPI.getBrands();
      const brands = response.data;

      commit(types.SET_BRANDS, brands);
      commit(types.SET_ORDER_UI_FLAG, { fetchingBrands: false });
      return brands;
    } catch (error) {
      commit(types.SET_ORDER_UI_FLAG, { fetchingBrands: false });
      return throwErrorMessage(error);
    }
  },
  async getCountries({ commit }) {
    commit(types.SET_ORDER_UI_FLAG, { fetchingCountries: true });
    try {
      const response = await GiftCardAPI.getCountries();
      const countries = response.data;

      commit(types.SET_COUNTRIES, countries);
      commit(types.SET_ORDER_UI_FLAG, { fetchingCountries: false });
    } catch (error) {
      commit(types.SET_ORDER_UI_FLAG, { fetchingCountries: false });
      return throwErrorMessage(error);
    }
  },
  async getBankList({ commit }, { country_id } = {}) {
    commit(types.SET_ORDER_UI_FLAG, { fetchingBankList: true });
    try {
      const response = await GiftCardAPI.getBankList(country_id);
      const banks = response.data;

      commit(types.SET_BANK_LIST, banks);
      commit(types.SET_ORDER_UI_FLAG, { fetchingBankList: false });
    } catch (error) {
      commit(types.SET_ORDER_UI_FLAG, { fetchingBankList: false });
      return throwErrorMessage(error);
    }
  },
  async getRateList({ commit }, { country_id, conversation_id } = {}) {
    commit(types.SET_ORDER_UI_FLAG, { fetchingRateList: true });
    try {
      const response = await GiftCardAPI.getRateList(country_id, conversation_id);
      commit(types.SET_RATE_LIST, response.data);
      commit(types.SET_ORDER_UI_FLAG, { fetchingRateList: false });
    } catch (error) {
      commit(types.SET_ORDER_UI_FLAG, { fetchingRateList: false });
      return throwErrorMessage(error);
    }
  },
  async getMemberDetails({ commit }, { contact_id, user_id = null } = {}) {
    commit(types.SET_ORDER_UI_FLAG, { fetchingMemberDetails: true });
    try {
      const response = await GiftCardAPI.getMemberDetails(contact_id, user_id);
      commit(types.SET_MEMBER_DETAILS, response.data);
      commit(types.SET_ORDER_UI_FLAG, { fetchingMemberDetails: false });
    } catch (error) {
      commit(types.SET_ORDER_UI_FLAG, { fetchingMemberDetails: false });
      return throwErrorMessage(error);
    }
  },
  // eslint-disable-next-line consistent-return
  async createOrder({ commit }, orderData) {
    commit(types.SET_ORDER_UI_FLAG, { creatingItem: true });
    try {
      const response = await GiftCardAPI.createOrder(orderData);
      commit(types.ADD_ORDER, response.data);
      commit(types.SET_ORDER_UI_FLAG, { creatingItem: false });
      return response.data;
    } catch (error) {
      commit(types.SET_ORDER_UI_FLAG, { creatingItem: false });
      return throwErrorMessage(error);
    }
  },
  async getOrderList({ commit }, queryData) {
    commit(types.SET_ORDER_UI_FLAG, { fetchingOrderList: true });
    try {
      const response = await GiftCardAPI.orderList(queryData);
      commit(types.SET_ORDER_LIST, response.data);
      commit(types.SET_ORDER_UI_FLAG, { fetchingOrderList: false });
      return response.data;
    } catch (error) {
      commit(types.SET_ORDER_UI_FLAG, { fetchingOrderList: false });
      return throwErrorMessage(error);
    }
  },
  // eslint-disable-next-line consistent-return
  async getOrderDetails({ commit }, { id } = {}) {
    commit(types.SET_ORDER_UI_FLAG, { fetchingOrderDetails: true });
    try {
      const response = await GiftCardAPI.getOrderDetails(id);
      commit(types.SET_ORDER_DETAILS, response.data);
      commit(types.SET_ORDER_UI_FLAG, { fetchingOrderDetails: false });
    } catch (error) {
      commit(types.SET_ORDER_UI_FLAG, { fetchingOrderDetails: false });
      return throwErrorMessage(error);
    }
  },
  async orderWithdraw({ commit }, data) {
    commit(types.SET_ORDER_UI_FLAG, { creatingOrderWithdraw: true });
    try {
      const response = await GiftCardAPI.orderWithdraw(data);
      commit(types.SET_ORDER_UI_FLAG, { creatingOrderWithdraw: false });
      return response.data;
    } catch (error) {
      commit(types.SET_ORDER_UI_FLAG, { creatingOrderWithdraw: false });
      return throwErrorMessage(error);
    }
  },
  async userWithdraw({ commit }, data) {
    commit(types.SET_ORDER_UI_FLAG, { creatingUserWithdraw: true });
    try {
      const response = await GiftCardAPI.userWithdraw(data);
      commit(types.SET_ORDER_UI_FLAG, { creatingUserWithdraw: false });
      return response.data;
    } catch (error) {
      commit(types.SET_ORDER_UI_FLAG, { creatingUserWithdraw: false });
      return throwErrorMessage(error);
    }
  },
  async notCard({ commit }, { id } = {}) {
    try {
      const response = await GiftCardAPI.notGiftCard(id);
      return response;
    } catch (error) {
      return throwErrorMessage(error);
    }
  },
  async getUserList({ commit }, { conversation_id } = {}) {
    try {
      commit(types.SET_ORDER_UI_FLAG, { fetchingUserList: true });
      const response = await GiftCardAPI.getUserList(conversation_id);
      commit(types.SET_USER_LIST, response.data);
      commit(types.SET_ORDER_UI_FLAG, { fetchingUserList: false });
      return response;
    } catch (error) {
      commit(types.SET_ORDER_UI_FLAG, { fetchingUserList: false });
      return throwErrorMessage(error);
    }
  },
};

const mutations = {
  [types.SET_ORDER_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.SET_COUNTRIES](_state, countries) {
    _state.countries = countries;
  },

  [types.SET_BRANDS](_state, brands) {
    _state.brands = brands;
  },

  [types.SET_BANK_LIST](_state, banks) {
    _state.bankList = banks;
  },

  [types.SET_RATE_LIST](_state, rateList) {
    _state.rateList = rateList;
  },

  [types.SET_MEMBER_DETAILS](_state, member) {
    _state.memberDetails = member;
  },

  [types.ADD_ORDER]: MutationHelpers.create,

  [types.SET_ORDER_LIST](_state, orderList) {
    _state.orderList = orderList;
  },

  [types.SET_ORDER_DETAILS](_state, details) {
    _state.orderDetails = details;
  },

  [types.SET_USER_LIST](_state, userList) {
    _state.userList = userList;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
